import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, History, Settings, CreditCard, LogOut,
  Leaf, Menu, X, User, ChevronRight,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", emoji: "🌿" },
  { to: "/history", icon: History, label: "My Results", emoji: "📋" },
  { to: "/payments", icon: CreditCard, label: "Subscription", emoji: "💳" },
  { to: "/settings", icon: Settings, label: "My Profile", emoji: "⚙️" },
];

function NavItem({ to, icon: Icon, label, emoji, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3.5 rounded-xl font-700 text-base transition-all duration-150 ${
          isActive
            ? "bg-brand-600 text-white shadow-md"
            : "text-brand-700 hover:bg-brand-100 hover:text-brand-800"
        }`
      }
    >
      <span className="text-xl w-7 text-center">{emoji}</span>
      <span>{label}</span>
      <ChevronRight size={14} className="ml-auto opacity-50" />
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => { logout(); navigate("/login"); };

  const SidebarContent = ({ onClose }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-brand-100">
        <div className="bg-brand-600 text-white rounded-xl w-10 h-10 flex items-center justify-center shadow">
          <Leaf size={22} />
        </div>
        <div>
          <div className="font-900 text-brand-800 text-lg leading-none">AgriHealth</div>
          <div className="text-xs font-700 text-brand-400 leading-none mt-0.5">Plant & Animal AI</div>
        </div>
        {onClose && (
          <button onClick={onClose} className="ml-auto text-brand-400 hover:text-brand-700 lg:hidden">
            <X size={22} />
          </button>
        )}
      </div>

      {/* User card */}
      <div className="mx-3 mt-4 mb-2 bg-brand-50 rounded-xl p-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-brand-600 text-white font-900 text-lg flex items-center justify-center shadow">
          {user?.avatar || user?.name?.[0] || "F"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-800 text-brand-800 text-sm truncate">{user?.name || "Farmer"}</div>
          <div className="text-xs font-600 text-brand-400 truncate">{user?.plan === "premium" ? "⭐ Premium" : "Free Plan"}</div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex flex-col gap-1 px-3 py-2 flex-1">
        {NAV.map((item) => (
          <NavItem key={item.to} {...item} onClick={onClose} />
        ))}
      </nav>

      {/* Logout */}
      <div className="px-3 pb-4 border-t border-brand-100 pt-3">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-red-500 hover:bg-red-50 font-700 transition-all"
        >
          <span className="text-xl w-7 text-center">🚪</span>
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-brand-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-shrink-0 bg-white border-r border-brand-100 flex-col shadow-sm">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-2xl flex flex-col">
            <SidebarContent onClose={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile topbar */}
        <header className="lg:hidden flex items-center gap-3 px-4 py-3 bg-white border-b border-brand-100 shadow-sm">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 rounded-xl text-brand-600 hover:bg-brand-50"
          >
            <Menu size={24} />
          </button>
          <div className="flex items-center gap-2">
            <div className="bg-brand-600 text-white rounded-lg w-8 h-8 flex items-center justify-center">
              <Leaf size={16} />
            </div>
            <span className="font-900 text-brand-800 text-lg">AgriHealth</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-brand-600 text-white font-900 flex items-center justify-center text-sm">
              {user?.avatar || user?.name?.[0] || "F"}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
